import { typeGuard } from '@shared/utils/type-guard';
import { BehaviorSubject, defer, filter, finalize, identity, Observable, of, switchMap, take, tap } from 'rxjs';

export interface AbstractCacheType<T> {
    /**
     * Observable emitting the current values of the cache
     */
    valueObs: Observable<T | null>;

    /**
     * Current value of the cache
     */
    value: T | null;

    /**
     * Observable emitting whether the cache is currently updating
     */
    loadingObs: Observable<boolean>;

    /**
     * Whether the cache is currently being updated
     */
    loading: boolean;

    /**
     * Observable emitting cache update errors
     */
    errorObs: Observable<any>;

    /**
     * Cache update error
     */
    error: any;

    /**
     * Last time the cache was successfully updated
     */
    lastSuccessfulUpdate: Date;

    /**
     * Age of the cache, i.e. how long ago it was last updated
     */
    age: number;

    /**
     * Update the cache and push only the updated value. If older values are acceptable, see `maxAge`. If it is currently being updated, see `forceUpdate`.
     * @param maxAge Max age in milliseconds of the cache. Default `0`.
     * @param forceUpdate If `true`, starts a new update even if the cache is is currently being updated. Otherwise, waits for it to update, does not start its own update and emits the current update's value. Default `false`.
     */
    update(maxAge: number, forceUpdate: boolean): Observable<T | null>;

    /**
     * Update the cache and keep pushing new values (equivalent to listening to `valueObs` and running `update()`). If older values are acceptable, see `maxAge`. If it is currently being updated, see `forceUpdate`.
     * @param maxAge Max age in milliseconds of the cache. Default `0`.
     * @param forceUpdate If `true`, starts a new update even if the cache is is currently being updated. Otherwise, waits for it to update, does not start its own update and emits the current update's value. Default `false`.
     */
    updatePush(maxAge: number, forceUpdate: boolean): Observable<T | null>;
}

export abstract class AbstractCache<T> implements AbstractCacheType<T> {
    private readonly _valueSource = new BehaviorSubject<T | null>(null);
    public readonly valueObs = this._valueSource.asObservable();

    private readonly _loadingSource = new BehaviorSubject<boolean>(false);
    public readonly loadingObs = this._loadingSource.asObservable();

    private readonly _errorSource = new BehaviorSubject<any>(null);
    public readonly errorObs = this._errorSource.asObservable();

    private _lastSuccessfulUpdate = 0;

    /**
     * Get the deserialized cache value from the server or some other source
     */
    protected abstract _getCurrentValue(): Observable<T>;

    constructor() {}

    public get value() {
        return this._valueSource.getValue();
    }

    public get loading(): boolean {
        return this._loadingSource.getValue();
    }

    public get error(): any {
        return this._errorSource.getValue();
    }

    public get lastSuccessfulUpdate(): Date {
        return new Date(this._lastSuccessfulUpdate);
    }

    public get age(): number {
        return Date.now() - this._lastSuccessfulUpdate;
    }

    public update(maxAge = 0, forceUpdate = false) {
        return this._update(maxAge, forceUpdate);
    }

    public updatePush(maxAge = 0, forceUpdate = false) {
        return this._update(maxAge, forceUpdate, false);
    }

    private _update(maxAge: number = 0, forceUpdate: boolean = false, takeOne: boolean = true) {
        if (!forceUpdate && this.loading) {
            return this.loadingObs.pipe(
                filter(typeGuard((l) => (!l ? l : undefined))),
                switchMap((loading: false) => this.valueObs.pipe(takeOne ? take(1) : identity)),
            );
        }

        if (this.value && this.age < Math.max(0, maxAge)) {
            return takeOne ? of(this.value) : this.valueObs;
        }

        return defer(() => {
            this._loadingSource.next(true);

            return this._getCurrentValue().pipe(
                take(1),
                tap({
                    next: (value) => {
                        this._lastSuccessfulUpdate = Date.now();
                        this._valueSource.next(value);
                        this._errorSource.next(null);
                    },
                    error: (error) => {
                        this._errorSource.next(error);
                    },
                }),
                finalize(() => {
                    this._loadingSource.next(false);
                }),
                takeOne ? identity : switchMap(() => this.valueObs),
            );
        });
    }
}
